/**
 * Copyright 2019 CANAL+ Group
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { AsyncSubject } from "rxjs";
import EventEmitter from "../../../utils/event_emitter";
import PPromise from "../../../utils/promise";
import { setUpDb } from "./api/db/dbSetUp";
import DownloadManager from "./api/downloader/downloadManager";
import { getBuilderFormattedForAdaptations, getBuilderFormattedForSegments, getKeySystemsSessionsOffline, offlineManifestLoader, } from "./api/downloader/manifest";
import { checkForPauseAMovie, checkForResumeAPausedMovie, checkInitDownloaderOptions, IndexDBError, isSupported, SegmentConstuctionError, ValidationArgsError, } from "./utils";
/**
 * Instanciate a D2G downloader.
 * @param {Object<{nameDB, storeManifestEvery}>} IOptionsStarter
 * @return {IPublicAPI} IPublicAPI
 */
var D2G = /** @class */ (function (_super) {
    __extends(D2G, _super);
    function D2G(options) {
        if (options === void 0) { options = {}; }
        var _this = _super.call(this) || this;
        _this.nameDB =
            options.nameDB == null || options.nameDB === ""
                ? "d2g-canalplus"
                : options.nameDB;
        _this.activeDownloads = {};
        _this.activePauses = {};
        _this.db = null;
        _this.emitter = {
            trigger: function (eventName, payload) { return _this.trigger(eventName, payload); },
        };
        return _this;
    }
    D2G.prototype.isD2GSupportedForEncryption = function () {
        return isSupported();
    };
    /**
     * Initialize an IndexDB instance.
     * @returns {Promise<void>}
     */
    D2G.prototype.initDB = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        setUpDb(_this.nameDB)
                            .then(function (db) {
                            _this.db = db;
                            resolve(db);
                        })
                            .catch(function (error) {
                            _this.trigger("error", { action: "initDB", error: error });
                            reject(error);
                        });
                    })];
            });
        });
    };
    /**
     * Start a download from scratch.
     * @param {Object<ISettingsDownloader>} settings
     * @returns {Promise.<void>}
     */
    D2G.prototype.download = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var db_1, metaData_1, contentID_1, transport_1, pause$, downloadManager, initDownloadSub, error_1, err;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        db_1 = this.db;
                        if (db_1 === null) {
                            throw new Error("The IndexDB database has not been created!");
                        }
                        return [4 /*yield*/, checkInitDownloaderOptions(options, db_1, this.activeDownloads)];
                    case 1:
                        _a.sent();
                        metaData_1 = options.metaData, contentID_1 = options.contentID, transport_1 = options.transport;
                        pause$ = new AsyncSubject();
                        this.activePauses[contentID_1] = pause$;
                        downloadManager = new DownloadManager({
                            emitter: this.emitter,
                            db: db_1,
                        });
                        initDownloadSub = downloadManager
                            .initDownload(options, pause$)
                            .subscribe(function (_a) {
                            var download = _a[0];
                            if (download === null) {
                                return;
                            }
                            var progress = download.progress, manifest = download.manifest, audio = download.audio, video = download.video, text = download.text, size = download.size;
                            if (manifest === null) {
                                return;
                            }
                            db_1.put("manifests", {
                                contentID: contentID_1,
                                transport: transport_1,
                                manifest: manifest,
                                builder: { video: video, audio: audio, text: text },
                                progress: progress,
                                size: size,
                                duration: manifest.getDuration(),
                                metaData: metaData_1,
                            }).then(function () {
                                return _this.trigger("insertDB", {
                                    action: "store",
                                    contentID: contentID_1,
                                    progress: progress.percentage,
                                });
                            }).catch(function (err) {
                                _this.trigger("error", {
                                    action: "resume-downloader",
                                    error: err,
                                    contentID: contentID_1,
                                });
                            });
                        }, function (error) { return _this.trigger("error", { action: "init-downloader", error: error }); });
                        this.activeDownloads[contentID_1] = initDownloadSub;
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        err = error_1;
                        this.trigger("error", {
                            action: "init-downloader",
                            error: err,
                            contentID: options.contentID,
                        });
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Resume a download already started earlier.
     * @param {string} contentID
     * @returns {Promise.<void>}
     */
    D2G.prototype.resume = function (contentID) {
        return __awaiter(this, void 0, void 0, function () {
            var db_2, storedManifest_1, pause$, downloadManager, resumeDownloadSub, err_1, error;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        db_2 = this.db;
                        if (db_2 === null) {
                            throw new Error("The IndexDB database has not been created!");
                        }
                        if (contentID == null || contentID === "") {
                            throw new Error("You must specify a valid contentID for resuming.");
                        }
                        return [4 /*yield*/, db_2.get("manifests", contentID)];
                    case 1:
                        storedManifest_1 = _a.sent();
                        checkForResumeAPausedMovie(storedManifest_1, this.activeDownloads);
                        pause$ = new AsyncSubject();
                        this.activePauses[contentID] = pause$;
                        downloadManager = new DownloadManager({
                            emitter: this.emitter,
                            db: db_2,
                        });
                        resumeDownloadSub = downloadManager
                            .resumeDownload(storedManifest_1, pause$)
                            .subscribe(function (_a) {
                            var download = _a[0];
                            if (download === null) {
                                return;
                            }
                            var progress = download.progress, manifest = download.manifest, audio = download.audio, video = download.video, text = download.text, size = download.size;
                            if (manifest === null) {
                                return;
                            }
                            var metaData = storedManifest_1.metaData, transport = storedManifest_1.transport, duration = storedManifest_1.duration;
                            db_2.put("manifests", {
                                contentID: contentID,
                                transport: transport,
                                manifest: manifest,
                                builder: { video: video, audio: audio, text: text },
                                progress: progress,
                                size: size,
                                duration: duration,
                                metaData: metaData,
                            }).then(function () {
                                return _this.trigger("insertDB", {
                                    action: "store",
                                    contentID: contentID,
                                    progress: progress.percentage,
                                });
                            }).catch(function (error) {
                                _this.trigger("error", {
                                    action: "resume-downloader",
                                    error: error,
                                    contentID: contentID,
                                });
                            });
                        }, function (error) {
                            return _this.trigger("error", {
                                action: "resume-downloader",
                                error: error,
                                contentID: contentID,
                            });
                        });
                        this.activeDownloads[contentID] = resumeDownloadSub;
                        return [3 /*break*/, 3];
                    case 2:
                        err_1 = _a.sent();
                        error = err_1;
                        this.trigger("error", { action: "resume-downloader", error: error, contentID: contentID });
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Pause a download already started earlier.
     * @param {string} contentID
     * @returns {void}
     */
    D2G.prototype.pause = function (contentID) {
        try {
            checkForPauseAMovie(contentID);
            var activeDownloads = this.activeDownloads;
            var activePauses = this.activePauses;
            if (activeDownloads[contentID] == null && activePauses[contentID] == null) {
                throw new ValidationArgsError("Invalid contentID given: " + contentID);
            }
            activePauses[contentID].next();
            activePauses[contentID].complete();
            activeDownloads[contentID].unsubscribe();
            delete activeDownloads[contentID];
            delete activePauses[contentID];
        }
        catch (e) {
            var error = e;
            this.trigger("error", {
                action: "pause",
                contentID: contentID,
                error: error !== undefined ? error : new Error("A Unexpected error happened"),
            });
        }
    };
    /**
     * Get all the downloaded entry (manifest) partially or fully downloaded.
     * @returns {Promise.<IStoredManifest[]|void>}
     */
    D2G.prototype.getAllOfflineContent = function (limit) {
        try {
            if (this.db == null) {
                throw new Error("The IndexDB database has not been created!");
            }
            // TODO: return formatted content ?
            return this.db.getAll("manifests", undefined, limit);
        }
        catch (e) {
            var error = e;
            this.trigger("error", {
                action: "getAllDownloadedMovies",
                error: new IndexDBError(error.message),
            });
        }
    };
    /**
     * Get a singleMovie ready to be played by the rx-player,
     * could be fully or partially downloaded.
     * @param {string} contentID
     * @returns {Promise.<IContentLoader|void>}
     */
    D2G.prototype.getSingleContent = function (contentID) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, contentManifest, contentsProtection, progress, transport, size, metaData, manifest, duration, contentProtection, e_1, error;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        if (this.db == null) {
                            throw new Error("The IndexDB database has not been created!");
                        }
                        return [4 /*yield*/, PPromise.all([
                                this.db.get("manifests", contentID),
                                this.db
                                    .transaction("contentsProtection", "readonly")
                                    .objectStore("contentsProtection")
                                    .index("contentID")
                                    .getAll(IDBKeyRange.only(contentID)),
                            ])];
                    case 1:
                        _a = _b.sent(), contentManifest = _a[0], contentsProtection = _a[1];
                        if (contentManifest === undefined || contentManifest.manifest === null) {
                            throw new SegmentConstuctionError("No Manifest found for current content " + contentID);
                        }
                        progress = contentManifest.progress, transport = contentManifest.transport, size = contentManifest.size, metaData = contentManifest.metaData, manifest = contentManifest.manifest, duration = contentManifest.duration;
                        contentProtection = getKeySystemsSessionsOffline(contentsProtection);
                        return [2 /*return*/, {
                                progress: progress,
                                size: size,
                                transport: transport,
                                contentID: contentID,
                                metaData: metaData,
                                contentProtection: contentProtection,
                                offlineManifest: offlineManifestLoader(manifest, getBuilderFormattedForAdaptations(contentManifest), getBuilderFormattedForSegments(contentManifest), { contentID: contentID, duration: duration, isFinished: progress.percentage === 100, db: this.db }),
                            }];
                    case 2:
                        e_1 = _b.sent();
                        error = e_1;
                        this.trigger("error", {
                            action: "getSingleMovie",
                            contentID: contentID,
                            error: error !== undefined ? error : new Error("A Unexpected error happened"),
                        });
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    D2G.prototype.getAvailableSpaceOnDevice = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, quota, usage;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (navigator.storage == null || navigator.storage.estimate == null) {
                            return [2 /*return*/, null];
                        }
                        return [4 /*yield*/, navigator.storage.estimate()];
                    case 1:
                        _a = _b.sent(), quota = _a.quota, usage = _a.usage;
                        if (quota === undefined || usage === undefined) {
                            return [2 /*return*/, null];
                        }
                        return [2 /*return*/, {
                                total: quota / 1e6,
                                used: usage / 1e6,
                            }];
                }
            });
        });
    };
    /**
     * Delete an entry partially or fully downloaded and stop the download
     * if the content is downloading, then delete.
     * @param {string} contentID
     * @returns {Promise.<void>}
     */
    D2G.prototype.deleteContent = function (contentID) {
        return __awaiter(this, void 0, void 0, function () {
            var activeDownloads, activePauses, db, indexTxSEG, cursor, indexTxDRM, cursorDRM, e_2, error;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 12, , 13]);
                        activeDownloads = this.activeDownloads;
                        activePauses = this.activePauses;
                        db = this.db;
                        if (db == null) {
                            throw new Error("The IndexDB database has not been created!");
                        }
                        if (activeDownloads[contentID] != null && activePauses[contentID] != null) {
                            activePauses[contentID].next();
                            activePauses[contentID].complete();
                            activeDownloads[contentID].unsubscribe();
                            delete activeDownloads[contentID];
                            delete activePauses[contentID];
                        }
                        indexTxSEG = db
                            .transaction("segments", "readwrite")
                            .objectStore("segments")
                            .index("contentID");
                        return [4 /*yield*/, indexTxSEG.openCursor(IDBKeyRange.only(contentID))];
                    case 1:
                        cursor = _a.sent();
                        _a.label = 2;
                    case 2:
                        if (!(cursor !== null)) return [3 /*break*/, 5];
                        return [4 /*yield*/, cursor.delete()];
                    case 3:
                        _a.sent();
                        return [4 /*yield*/, cursor.continue()];
                    case 4:
                        cursor = _a.sent();
                        return [3 /*break*/, 2];
                    case 5:
                        indexTxDRM = db
                            .transaction("contentsProtection", "readwrite")
                            .objectStore("contentsProtection")
                            .index("contentID");
                        return [4 /*yield*/, indexTxDRM.openCursor(IDBKeyRange.only(contentID))];
                    case 6:
                        cursorDRM = _a.sent();
                        _a.label = 7;
                    case 7:
                        if (!(cursorDRM !== null)) return [3 /*break*/, 10];
                        return [4 /*yield*/, cursorDRM.delete()];
                    case 8:
                        _a.sent();
                        return [4 /*yield*/, cursorDRM.continue()];
                    case 9:
                        cursorDRM = _a.sent();
                        return [3 /*break*/, 7];
                    case 10: return [4 /*yield*/, db.delete("manifests", contentID)];
                    case 11:
                        _a.sent();
                        return [3 /*break*/, 13];
                    case 12:
                        e_2 = _a.sent();
                        error = e_2;
                        this.trigger("error", {
                            action: "delete-download",
                            contentID: contentID,
                            error: error !== undefined ? error : new Error("A Unexpected error happened"),
                        });
                        return [3 /*break*/, 13];
                    case 13: return [2 /*return*/];
                }
            });
        });
    };
    return D2G;
}(EventEmitter));
export default D2G;
